import React from 'react';
import { LabelLayout, PackageRow } from '@/services/api/packages';
import { LabelQR } from './LabelQR';
import {
  DASH_LOGO_PATHS,
  DASH_LOGO_RATIO,
  DASH_LOGO_VIEWBOX,
  LABEL_FIELD,
} from './labelConstants';

interface LabelPreviewProps {
  pkg: PackageRow;
  layout?: LabelLayout;
}

// Dash wordmark (vector) — used on thermal labels so it prints crisp + solid
// black, matching the PDF. Already includes the "DASH" lettering.
const DashWordmark: React.FC<{ height: number }> = ({ height }) => (
  <svg
    viewBox={DASH_LOGO_VIEWBOX}
    height={height}
    width={height * DASH_LOGO_RATIO}
    fill="#0E121B"
    style={{ display: 'block' }}
  >
    {DASH_LOGO_PATHS.map((d, i) => (
      <path key={i} d={d} fillRule="evenodd" clipRule="evenodd" />
    ))}
  </svg>
);

// Client names vary wildly in length. Step the header font down for long names
// so they wrap onto a tidy second line instead of overflowing the label width.
const clientFontSize = (name: string, base: number, small: number): number =>
  name.length > 20 ? small : base;

// ─── Square 100×100 preview — Astro-style layout ─────────────────────────────
const SQ_INK = '#0E121B';
const sqLabel: React.CSSProperties = {
  fontSize: 6.5,
  fontWeight: 800,
  color: SQ_INK,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};
const sqAddr: React.CSSProperties = {
  fontSize: 7.5,
  fontWeight: 700,
  color: SQ_INK,
  lineHeight: 1.25,
  marginTop: 1,
  wordBreak: 'break-word',
};

const LabelPreviewSquare: React.FC<{ pkg: PackageRow }> = ({ pkg }) => (
  <div
    className="bg-white flex flex-col font-sans flex-shrink-0"
    style={{
      width: 300,
      minHeight: 300,
      border: '1.5px solid #0E121B',
      borderRadius: 5,
      padding: 12,
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      filter: 'grayscale(1)',
    }}
  >
    {/* Header — Dash wordmark · client name */}
    <div
      className="flex items-start pb-1.5"
      style={{ borderBottom: '1.5px solid #0E121B' }}
    >
      <DashWordmark height={15} />
      {pkg.client?.name && (
        <div
          className="flex min-w-0 items-start text-left"
          style={{
            marginLeft: 4,
            // lineHeight == wordmark height so the first line's box matches the
            // logo box; with items-start the client name (any font size) sits
            // optically centred with the wordmark, and wrapped lines still
            // hang off the first line.
            fontSize: clientFontSize(pkg.client.name, 10, 8),
            fontWeight: 800,
            color: SQ_INK,
            lineHeight: '15px',
          }}
        >
          <span className="flex-shrink-0" style={{ marginRight: 6 }}>
            |
          </span>
          <span className="min-w-0 break-words">{pkg.client.name}</span>
        </div>
      )}
    </div>

    {/* Recipient name + booking ID (left) · X/Y badge + item name (right) */}
    <div className="flex py-2">
      <div className="flex-1 min-w-0" style={{ paddingRight: 6 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: SQ_INK,
            lineHeight: 1.1,
            wordBreak: 'break-word',
          }}
        >
          {pkg.outlet}
        </div>
        {pkg.bookingId && (
          <div
            className="font-mono"
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: SQ_INK,
              marginTop: 3,
            }}
          >
            #{pkg.bookingId}
          </div>
        )}
      </div>
      <div
        className="flex flex-col items-end flex-shrink-0"
        style={{ width: 96 }}
      >
        {pkg.itemTotal ? (
          <div
            style={{
              border: '1.5px solid #0E121B',
              borderRadius: 5,
              padding: '2px 8px',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 800, color: SQ_INK }}>
              {pkg.itemSeq} / {pkg.itemTotal}
            </span>
          </div>
        ) : null}
        {pkg.itemName && (
          <div
            style={{
              fontSize: 7.5,
              fontWeight: 700,
              color: SQ_INK,
              marginTop: 4,
              textAlign: 'right',
              lineHeight: 1.2,
              wordBreak: 'break-word',
            }}
          >
            {pkg.itemName}
          </div>
        )}
      </div>
    </div>

    {/* Address (left) + QR (right) */}
    <div
      className="flex flex-1"
      style={{ borderTop: '1px solid #0E121B', paddingTop: 6 }}
    >
      <div className="flex-1 min-w-0" style={{ paddingRight: 8 }}>
        <span style={sqLabel}>{LABEL_FIELD.to}</span>
        {pkg.recipientAddress && (
          <div style={sqAddr}>{pkg.recipientAddress}</div>
        )}
        <div style={{ marginTop: 6 }}>
          <span style={sqLabel}>{LABEL_FIELD.from}</span>
          <div style={sqAddr}>{pkg.sender}</div>
          {pkg.senderAddress && <div style={sqAddr}>{pkg.senderAddress}</div>}
        </div>
      </div>
      <div
        className="flex flex-col items-center flex-shrink-0"
        style={{ width: 104 }}
      >
        <LabelQR waybill={pkg.itemName ?? pkg.deliveryId} size={100} />
      </div>
    </div>

    {/* Footer: waybill · qty/weight */}
    <div
      className="flex justify-between mt-1.5 pt-1.5"
      style={{ borderTop: '1px dashed #ccc' }}
    >
      {pkg.deliveryId && (
        <div className="flex flex-col min-w-0">
          <span style={sqLabel}>{LABEL_FIELD.waybill}</span>
          <span
            className="font-mono"
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: SQ_INK,
              wordBreak: 'break-all',
              marginTop: 1,
            }}
          >
            {pkg.deliveryId}
          </span>
        </div>
      )}
      <div className="flex flex-col items-end flex-shrink-0">
        <span style={sqLabel}>{LABEL_FIELD.qtyWeight}</span>
        <span
          style={{ fontSize: 9, fontWeight: 700, color: SQ_INK, marginTop: 1 }}
        >
          {pkg.qty} pcs · {pkg.weight} kg
        </span>
      </div>
    </div>
  </div>
);

// ─── Full / A4 preview ────────────────────────────────────────────────────────
const LabelPreviewFull: React.FC<{ pkg: PackageRow; isThermal: boolean }> = ({
  pkg,
  isThermal,
}) => (
  <div
    className="bg-white flex flex-col font-sans flex-shrink-0"
    style={{
      width: 300,
      minHeight: 450,
      border: '1.5px solid #0E121B',
      borderRadius: 5,
      padding: '16px 16px 12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      filter: isThermal ? 'grayscale(1)' : undefined,
    }}
  >
    {/* Brand row — Dash lockup · client name */}
    <div
      className="flex items-start pb-2.5"
      style={{ borderBottom: '2px solid #0E121B' }}
    >
      {isThermal ? (
        <DashWordmark height={20} />
      ) : (
        <div className="flex items-center gap-1.5">
          <img
            src="/images/Dash-Official-Logo.png"
            alt="Dash"
            className="w-4 h-3 object-contain"
          />
          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '0.02em',
              color: '#0E121B',
            }}
          >
            DASH
          </span>
        </div>
      )}
      {pkg.client?.name && (
        <div
          className="flex min-w-0 items-start text-left"
          style={{
            marginLeft: 5,
            // lineHeight == the logo height so the first line's box matches
            // the logo box; with items-start the client name (any font size)
            // sits optically centred with the logo, and wrapped lines still
            // hang off the first line. Both the thermal wordmark and the A4
            // colour lockup (dominated by its DASH text) measure ~20 tall.
            fontSize: clientFontSize(pkg.client.name, 12, 9),
            fontWeight: 800,
            color: '#0E121B',
            lineHeight: '20px',
          }}
        >
          <span className="flex-shrink-0" style={{ marginRight: 6 }}>
            |
          </span>
          <span className="min-w-0 break-words">{pkg.client.name}</span>
        </div>
      )}
    </div>

    {/* QR + item name */}
    <div
      className="flex flex-col items-center gap-1.5 py-3"
      style={{ borderBottom: '1.5px dashed #0E121B' }}
    >
      <LabelQR waybill={pkg.itemName ?? pkg.deliveryId} size={108} />
      {pkg.itemName && (
        <span
          className="text-center"
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: '#0E121B',
            lineHeight: 1.2,
            wordBreak: 'break-word',
          }}
        >
          {pkg.itemName}
        </span>
      )}
    </div>

    {/* TO */}
    <div className="py-1.5" style={{ borderBottom: '1px dotted #ccc' }}>
      <span
        style={{
          fontSize: 7,
          letterSpacing: '0.08em',
          fontWeight: 800,
          color: '#0E121B',
          textTransform: 'uppercase',
        }}
      >
        {LABEL_FIELD.to}
      </span>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: '#0E121B',
          lineHeight: 1.2,
          marginTop: 1,
        }}
      >
        {pkg.outlet}
      </div>
      {pkg.recipientAddress && (
        <div
          style={{
            fontSize: 8.5,
            color: '#0E121B',
            fontWeight: 700,
            lineHeight: 1.35,
            marginTop: 2,
            wordBreak: 'break-word',
          }}
        >
          {pkg.recipientAddress}
        </div>
      )}
    </div>

    {/* FROM */}
    <div className="py-1.5" style={{ borderBottom: '1px dotted #ccc' }}>
      <span
        style={{
          fontSize: 7,
          letterSpacing: '0.08em',
          fontWeight: 800,
          color: '#0E121B',
          textTransform: 'uppercase',
        }}
      >
        {LABEL_FIELD.from}
      </span>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: '#0E121B',
          lineHeight: 1.2,
          marginTop: 1,
        }}
      >
        {pkg.sender}
      </div>
      {pkg.senderAddress && (
        <div
          style={{
            fontSize: 8.5,
            color: '#0E121B',
            fontWeight: 700,
            lineHeight: 1.35,
            marginTop: 2,
            wordBreak: 'break-word',
          }}
        >
          {pkg.senderAddress}
        </div>
      )}
    </div>

    {/* Key/value rows */}
    <div className="flex flex-col flex-1 pt-1">
      <div
        className="grid gap-2 py-1.5"
        style={{
          gridTemplateColumns: '80px 1fr',
          borderBottom: '1px dotted #ccc',
        }}
      >
        <span
          style={{
            fontSize: 7,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#0E121B',
            fontWeight: 800,
          }}
        >
          {LABEL_FIELD.qtyWeight}
        </span>
        <span
          style={{
            fontSize: 9.5,
            color: '#0E121B',
            fontWeight: 800,
            lineHeight: 1.3,
          }}
        >
          {pkg.qty} pcs · {pkg.weight} kg
        </span>
      </div>
      {pkg.deliveryId && (
        <div
          className="grid gap-2 py-1.5"
          style={{
            gridTemplateColumns: '80px 1fr',
            borderBottom: '1px dotted #ccc',
          }}
        >
          <span
            style={{
              fontSize: 7,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#0E121B',
              fontWeight: 800,
            }}
          >
            {LABEL_FIELD.waybill}
          </span>
          <span
            className="font-mono"
            style={{
              fontSize: 9.5,
              color: '#0E121B',
              fontWeight: 800,
              lineHeight: 1.3,
              wordBreak: 'break-all',
            }}
          >
            {pkg.deliveryId}
          </span>
        </div>
      )}
      {pkg.bookingId && (
        <div
          className="grid gap-2 py-1.5"
          style={{
            gridTemplateColumns: '80px 1fr',
            borderBottom: '1px dotted #ccc',
          }}
        >
          <span
            style={{
              fontSize: 7,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#0E121B',
              fontWeight: 800,
            }}
          >
            {LABEL_FIELD.bookingId}
          </span>
          <span
            className="font-mono"
            style={{
              fontSize: 9.5,
              color: '#0E121B',
              fontWeight: 800,
              lineHeight: 1.3,
              wordBreak: 'break-all',
            }}
          >
            {pkg.bookingId}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── Router ───────────────────────────────────────────────────────────────────
export const LabelPreview: React.FC<LabelPreviewProps> = ({ pkg, layout }) => {
  if (layout === 'THERMAL_100X100') {
    return <LabelPreviewSquare pkg={pkg} />;
  }
  return <LabelPreviewFull pkg={pkg} isThermal={layout !== 'A4_2UP'} />;
};
